import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditIcon, LockIcon } from '@/components/common/icon';
import { Alert } from 'aha-components';
import FairRangeEstimateHint from './FairRangeEstimateHint';
import EstimateFairRange, {
  getFairRangeDefaultValue,
  isEmptyFairRangeValue
} from './EstimatePriceComponents/EstimateFairRange';
import EstimateFairTips from './EstimatePriceComponents/EstimateFairTips';
import axiosInstance from '@/apis/axiosInstance';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import {
  getEstimatedRateLevel,
  isPositiveRate
} from './EstimatePriceComponents/rateValidation';

function formatPriceWithCommas(value: number | string | undefined | null) {
  if (value === '' || value === undefined || value === null) return '0';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export default function ShareRateInput({
  setNewOrderPrice,
  newOrderPrice,
  isCounterOffer = false,
  lastAdsOfferAmount,
  lastCreatorOfferAmount,
  disabled = false,
  visible,
  onPriceRangeValidChange,
  isAcceptOffer = false,
  readonlyPriceFromProp = false
}: {
  setNewOrderPrice: (price: number | string) => void;
  newOrderPrice: number | string;
  isCounterOffer?: boolean;
  lastAdsOfferAmount?: number | null;
  lastCreatorOfferAmount?: number;
  disabled?: boolean;
  visible?: boolean;
  onPriceRangeValidChange?: (isValid: boolean) => void;
  isAcceptOffer?: boolean;
  /** disabled 时直接展示 newOrderPrice，不读 selectedPriceFields / order.lastPrice */
  readonlyPriceFromProp?: boolean;
}) {
  const { t } = useTranslation();
  const {
    selectedPriceFields,
    creatorLowActualGet,
    creatorHighActualGet,
    creatorMaxActualGet,
    priceErrorShakeKey
  } = useMultiplePricesStore();
  const { detailData } = useDetailDataStore();
  const { order } = detailData;
  const { negotiationHistory } = order;
  const submitedBy =
    negotiationHistory?.find((item: any) => !!item.nonNegotiable)?.submitedBy ??
    null;
  const low = creatorLowActualGet;
  const high = creatorHighActualGet;
  const [isEditing, setIsEditing] = useState(false);
  const [editPriceShakeKey, setEditPriceShakeKey] = useState(0);
  const hasManuallyAdjustedRef = useRef(false);
  const hasSetCounterOfferInitialRef = useRef(false);
  const prevLowHighRef = useRef<{
    low: number | null;
    high: number | null;
  } | null>(null);
  const setNewOrderPriceFromUser = useCallback(
    (price: number | string) => {
      hasManuallyAdjustedRef.current = true;
      setNewOrderPrice(price);
    },
    [setNewOrderPrice]
  );
  const displayPrice = String(newOrderPrice ?? '') || '0';
  const lockedDisplayPrice = readonlyPriceFromProp
    ? newOrderPrice
    : isAcceptOffer
      ? (selectedPriceFields?.[0]?.actualGet ??
        selectedPriceFields?.[0]?.price ??
        newOrderPrice)
      : (selectedPriceFields?.[0]?.actualGet ??
        selectedPriceFields?.[0]?.amount ??
        selectedPriceFields?.[0]?.price ??
        order?.lastPrice ??
        newOrderPrice);
  const [setBaseRateSuccess, setSetBaseRateSuccess] = useState(false);
  const isCounterOfferPriceValid = isPositiveRate(newOrderPrice);
  const estimatedRateLevel = getEstimatedRateLevel(newOrderPrice, low, high);
  const hasPriceError =
    (isCounterOffer && !isCounterOfferPriceValid) ||
    estimatedRateLevel === 'error';
  const [activeErrorShakeKey, setActiveErrorShakeKey] = useState(0);
  const handledErrorShakeKeyRef = useRef(priceErrorShakeKey);

  useEffect(() => {
    if (!hasPriceError) {
      handledErrorShakeKeyRef.current = priceErrorShakeKey;
      setActiveErrorShakeKey(0);
      return;
    }
    if (handledErrorShakeKeyRef.current === priceErrorShakeKey) return;
    handledErrorShakeKeyRef.current = priceErrorShakeKey;
    setActiveErrorShakeKey(priceErrorShakeKey);
  }, [hasPriceError, priceErrorShakeKey]);

  const errorShakeClassName =
    activeErrorShakeKey > 0
      ? 'animate-edit-price-shake motion-reduce:animate-none'
      : '';

  useEffect(() => {
    onPriceRangeValidChange?.(disabled || !hasPriceError);
  }, [disabled, hasPriceError, onPriceRangeValidChange]);

  // 切换价格套餐后，重新对比价格提示
  useEffect(() => {
    // 价格限制不同，和更新价格后，重置base rate 设置成功状态
    setSetBaseRateSuccess(false);
  }, [newOrderPrice]);

  // 切换选中的订单类型后，重置base rate 设置成功状态
  useEffect(() => {
    setSetBaseRateSuccess(false);
  }, [selectedPriceFields]);

  // 议价场景：仅用达人上次出价初始化一次
  useEffect(() => {
    if (disabled) return;
    if (hasManuallyAdjustedRef.current || hasSetCounterOfferInitialRef.current)
      return;
    if (!isCounterOffer || isEmptyFairRangeValue(lastCreatorOfferAmount))
      return;

    setNewOrderPrice(Math.round(Number(lastCreatorOfferAmount)));
    hasSetCounterOfferInitialRef.current = true;
  }, [isCounterOffer, lastCreatorOfferAmount, setNewOrderPrice, disabled]);

  // 仅在 low/high 变更时重置公允区间默认值（切换内容形式后无论是否手动调价均重置）
  useEffect(() => {
    if (disabled) return;
    if (low == null || high == null) return;

    const prev = prevLowHighRef.current;
    const isLowHighChanged =
      prev == null
        ? !isCounterOffer || isEmptyFairRangeValue(lastCreatorOfferAmount)
        : prev.low !== low || prev.high !== high;

    prevLowHighRef.current = { low, high };

    if (!isLowHighChanged) return;

    hasManuallyAdjustedRef.current = false;
    setNewOrderPrice(getFairRangeDefaultValue(low, high));
  }, [
    low,
    high,
    setNewOrderPrice,
    isCounterOffer,
    lastCreatorOfferAmount,
    disabled
  ]);

  useEffect(() => {
    if (!visible) return;
    const interestedPriceField = readonlyPriceFromProp
      ? detailData?.order?.interestedPriceFields?.[0]
      : selectedPriceFields?.[0];
    if (!interestedPriceField || !detailData?.order?.id) {
      return;
    }
    const params = {
      interestedPriceField: {
        ...interestedPriceField
      }
    };
    axiosInstance
      .post(`/order/${detailData.order.id}/competitivePrice`, params)
      .then(({ data }) => {
        const interestedPriceField = data?.data?.interestedPriceField;
        useMultiplePricesStore.setState({
          creatorLowActualGet:
            interestedPriceField?.fairPriceSuggestion?.creatorLowActualGet ??
            null,
          creatorHighActualGet:
            interestedPriceField?.fairPriceSuggestion?.creatorHighActualGet ??
            null,
          creatorMaxActualGet: interestedPriceField?.creatorMaxActualGet ?? null
        });
      })
      .catch(() => {});
  }, [detailData, selectedPriceFields, visible, readonlyPriceFromProp]);

  const finishEditing = () => {
    const trimmed = String(newOrderPrice ?? '').trim();
    if (trimmed && +trimmed > 0) {
      setNewOrderPrice(Math.round(+trimmed));
    } else if (!trimmed) {
      setNewOrderPrice('');
    }
    setIsEditing(false);
  };

  const handlePriceInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setNewOrderPriceFromUser('');
      return;
    }
    if (/^\d+$/.test(value)) {
      setNewOrderPriceFromUser(value);
    }
  };

  const handleSliderBoundaryReach = useCallback(() => {
    setEditPriceShakeKey((key) => key + 1);
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 justify-between">
        <div className="justify-start text-[#0D0D0D] text-base font-semibold font-['Plus Jakarta Sans'] leading-snug">
          {isCounterOffer
            ? t('modal.modal_counter_offer.your_counter_rate', {
                defaultValue: 'Your counter offer'
              })
            : disabled && submitedBy === 'ads'
              ? t('price.fields.set_your_rate.brand_fixed_rate', {
                  defaultValue: 'Brand fixed rate'
                })
              : t('price.fields.set_your_rate.share_rate_title', {
                  defaultValue: 'Share your rate'
                })}
        </div>
        {low != null && high != null && <FairRangeEstimateHint />}
      </div>
      <div className="border border-[#E7E5E4] rounded-md p-4 flex flex-col gap-4">
        <div className="flex items-end justify-center gap-2">
          <div className="flex items-end gap-1">
            <span className="text-[#44403C] text-2xl font-semibold font-['DM_Sans'] leading-8">
              $
            </span>
            {isEditing && !disabled ? (
              <span className="relative inline-block">
                <span
                  aria-hidden
                  className="invisible block whitespace-pre text-[#44403C] text-5xl font-semibold font-['DM_Sans'] leading-none"
                >
                  {displayPrice}
                </span>
                <input
                  type="number"
                  autoFocus
                  step={1}
                  disabled={detailData?.order?.fixedCreatorEarnAmount}
                  value={newOrderPrice ?? ''}
                  className="absolute left-0 top-0 m-0 h-full w-full border-none bg-transparent p-0 text-[#44403C] text-5xl font-semibold font-['DM_Sans'] leading-none outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  onChange={handlePriceInputChange}
                  onWheel={(e) => e.currentTarget.blur()}
                  onBlur={finishEditing}
                />
              </span>
            ) : (
              <span
                className="text-[#44403C] text-5xl font-semibold font-['DM_Sans'] cursor-pointer"
                onClick={() => !disabled && setIsEditing(true)}
              >
                {disabled
                  ? formatPriceWithCommas(lockedDisplayPrice)
                  : formatPriceWithCommas(newOrderPrice)}
              </span>
            )}
          </div>
          {!isEditing && !disabled && (
            <button
              key={editPriceShakeKey}
              type="button"
              className={`inline-flex h-5 shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-sm border-0 bg-transparent p-0 font-['DM_Sans'] text-sm font-semibold leading-5 text-[#57534E] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FB6011] ${
                editPriceShakeKey > 0
                  ? 'animate-edit-price-shake motion-reduce:animate-none'
                  : ''
              }`}
              onClick={() => setIsEditing(true)}
            >
              <EditIcon className="h-5 w-5 text-[#A9A29D]" />
              {t('price.fields.set_your_rate.edit_price', {
                defaultValue: 'Edit price'
              })}
            </button>
          )}
          {!order?.isPriceNegotiable && disabled && (
            <div className="flex items-center gap-[2px] pb-1">
              <LockIcon className="w-5 h-5 text-[#A9A29D]" />
              <div className="justify-start text-[#737373] text-lg font-normal font-['Inter'] leading-5">
                {t('price.fields.set_your_rate.non_negotiable_label', {
                  defaultValue: 'Non-negotiable'
                })}
              </div>
            </div>
          )}
        </div>
        {low != null && high != null && (
          <EstimateFairRange
            low={low}
            high={high}
            value={newOrderPrice}
            setValue={setNewOrderPriceFromUser}
            lastAdsOfferAmount={lastAdsOfferAmount as number}
            lastCreatorOfferAmount={lastCreatorOfferAmount}
            disabled={disabled}
            onReachBoundary={handleSliderBoundaryReach}
          />
        )}
        {!disabled && isCounterOffer && !isCounterOfferPriceValid && (
          <div
            key={activeErrorShakeKey}
            className={errorShakeClassName}
            onAnimationEnd={() => setActiveErrorShakeKey(0)}
          >
            <Alert
              type="error"
              description={t(
                'modal.modal_counter_offer.positive_rate_required',
                {
                  defaultValue: 'Enter a rate greater than 0.'
                }
              )}
            />
          </div>
        )}
        {!disabled &&
          low != null &&
          high != null &&
          (!isCounterOffer || isCounterOfferPriceValid) && (
            <div
              key={hasPriceError ? activeErrorShakeKey : undefined}
              className={hasPriceError ? errorShakeClassName : undefined}
              onAnimationEnd={() => setActiveErrorShakeKey(0)}
            >
              <EstimateFairTips
                low={low}
                high={high}
                creatorMaxActualGet={creatorMaxActualGet}
                newOrderPrice={newOrderPrice as number | string}
                setBaseRateSuccess={setBaseRateSuccess}
                setSetBaseRateSuccess={setSetBaseRateSuccess}
              />
            </div>
          )}
        {!order?.isPriceNegotiable && disabled && (
          <Alert
            type="info"
            description={
              submitedBy === 'ads'
                ? t('price.fields.set_your_rate.non_negotiable_alert_brand', {
                    defaultValue:
                      'The brand set this rate as non-negotiable. You can accept or decline, but not counter.'
                  })
                : t('price.fields.set_your_rate.non_negotiable_alert_creator', {
                    defaultValue:
                      'You set this rate as non-negotiable. The brand can accept or decline, but not counter.'
                  })
            }
          />
        )}
      </div>
    </div>
  );
}
